import React from 'react';
import { useQuery } from '@apollo/client';
import { TabPanel, Tabs } from '@css-xp/base';
import {
  TermPaymentAuditTrailSearchParams,
  TermPaymentAuditTrailsSearchOptions,
  TermPaymentTypes,
  FetchTermPaymentAuditTrailsPpcQuery,
  FetchTermPaymentAuditTrailsPpcQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { InvoiceTable } from '@css-xp/ppc-invoice-table';
import { AuditNotesTable } from '@css-xp/ppc-audit-notes-table/src/audit-notes-table';
import { DEFAULT_PAGE_SIZE, refundedTabNameKey } from '@css-xp/ppc-constants';
import { fetchTermPaymentAuditTrailsPPC } from '../../queries/fetch-term-payment-audit-trails-ppc';
import './credit-details-tabs.scss';

interface CreditDetailsTabsProps {
  transactionAccountId: string;
  termType?: string;
  pushPaymentMethodId?: string | undefined | null;
  sfdcId?: string | null;
  isDesignIteration1Enabled?: boolean;
}

type RefetchParams = Partial<{
  searchOptions: TermPaymentAuditTrailsSearchOptions;
  searchParams: TermPaymentAuditTrailSearchParams;
}>;

const defaultSearchOptions: TermPaymentAuditTrailsSearchOptions = {
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
};

export const CreditDetailsTabs = ({
  transactionAccountId,
  termType,
  pushPaymentMethodId,
  sfdcId,
  isDesignIteration1Enabled = false,
}: CreditDetailsTabsProps) => {
  const deferredInvoicesFilterOptions = [
    {
      paymentMethodId: `${pushPaymentMethodId}`,
    },
  ];

  const getSearchParams = ({
    transactionAccountId,
  }: CreditDetailsTabsProps): TermPaymentAuditTrailSearchParams | undefined => {
    const transactionAccountKey = termType === TermPaymentTypes.NET14 ? transactionAccountId : sfdcId;
    return {
      transactionAccountKey,
    };
  };

  const searchParams = getSearchParams({ transactionAccountId });
  const searchOptions = defaultSearchOptions;

  const shouldSkipTheAuditTrailsQuery = !(termType === TermPaymentTypes?.NET14 ? !!transactionAccountId : !!sfdcId);

  const { data, error, loading, refetch } = useQuery<
    FetchTermPaymentAuditTrailsPpcQuery,
    FetchTermPaymentAuditTrailsPpcQueryVariables
  >(fetchTermPaymentAuditTrailsPPC, {
    variables: {
      searchParams,
      searchOptions,
    },
    displayName: 'fetchTermPaymentAuditTrailsPPC',
    notifyOnNetworkStatusChange: true,
    skip: shouldSkipTheAuditTrailsQuery,
  });

  const combinedRefetch = (refetchParams: RefetchParams) => {
    refetch({
      searchParams: { ...searchParams, ...refetchParams?.searchParams },
      searchOptions: { ...searchOptions, ...refetchParams?.searchOptions },
    });
  };

  const dataState = {
    loading: loading,
    error: error?.toString(),
    data: data?.termPaymentAuditTrails,
    refetch: combinedRefetch,
    totalCount: data?.termPaymentAuditTrails?.totalCount,
  };

  return (
    <div className="credit-details-page-tabs">
      <Tabs className="tab-container" testId="ppc-credit-details-tabs">
        <TabPanel label="Invoices" enabled={true}>
          <InvoiceTable
            label="Invoices"
            transactionAccountId={transactionAccountId}
            id={transactionAccountId}
            shouldShowDeferredInvoicesColumns={true}
            filters={deferredInvoicesFilterOptions}
            shouldHideCustomiseView={isDesignIteration1Enabled}
          />
        </TabPanel>
        {isDesignIteration1Enabled && (
          <TabPanel label="Refunds" enabled={true}>
            <InvoiceTable
              label="Refunds"
              transactionAccountId={transactionAccountId}
              id={transactionAccountId}
              shouldShowDeferredInvoicesColumns={true}
              filters={deferredInvoicesFilterOptions}
              selectedInterfaceKey={refundedTabNameKey}
              shouldHideCustomiseView={true}
            />
          </TabPanel>
        )}
        <TabPanel label="Audit trail" enabled={true}>
          <AuditNotesTable
            id={transactionAccountId}
            label="Audit trail"
            shouldShowTermPaymentAuditTrails={true}
            termPaymentAuditTrailsDataState={dataState}
            isDesignIteration1Enabled={isDesignIteration1Enabled}
          />
        </TabPanel>
      </Tabs>
    </div>
  );
};
